import type * as Sentry from "@sentry/react";

import type { IAppConfig } from "../config/parse";
import { isNoResponseSentryEvent } from "./isNoResponseError";
import {
  isThirdPartyScriptFrame,
  SENTRY_IGNORE_ERRORS,
} from "./sentryIgnoreErrors";

export interface IInitSentryOptions {
  /** Also drop AbortErrors, third-party script errors and known browser-injected noise. */
  filterBrowserNoise?: boolean;
}

/** Replay privacy switches for apps that record sessions, taken from Sentry's own options type. */
export type SentryReplayPrivacy = Required<
  Pick<
    NonNullable<Parameters<typeof Sentry.replayIntegration>[0]>,
    "maskAllText" | "maskAllInputs" | "blockAllMedia"
  >
>;

/**
 * Opt-in replay privacy defaults: spread them into `Sentry.replayIntegration`
 * and override single switches (partner: `maskAllInputs: false`).
 */
export const SENTRY_REPLAY_PRIVACY_DEFAULTS: SentryReplayPrivacy = {
  maskAllText: false,
  maskAllInputs: true,
  blockAllMedia: false,
};

export type SentryCommonOptions = Pick<
  Sentry.BrowserOptions,
  "environment" | "enabled" | "ignoreErrors" | "beforeSend"
>;

const isAbortError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  error.name === "AbortError";

const hasThirdPartyFrame = (event: Sentry.ErrorEvent): boolean =>
  event.exception?.values?.some((exception) =>
    exception.stacktrace?.frames?.some((frame) =>
      isThirdPartyScriptFrame(frame.filename),
    ),
  ) ?? false;

/**
 * Common part of every app's Sentry options: `environment` and `enabled` from
 * the config, and a `beforeSend` that drops requests that never got a
 * response. Each app builds the rest (DSN, sample rates, replay, ignore lists)
 * in its own entry file and calls `Sentry.init` with the merged options.
 */
export const initSentryOptions = (
  config: Pick<IAppConfig, "environment" | "isSentryEnabled">,
  options: IInitSentryOptions = {},
): SentryCommonOptions => {
  const { filterBrowserNoise = false } = options;

  return {
    environment: config.environment,
    enabled: config.isSentryEnabled,
    ignoreErrors: filterBrowserNoise ? SENTRY_IGNORE_ERRORS : undefined,
    beforeSend(event, hint) {
      if (isNoResponseSentryEvent(event, hint)) return null;

      if (!filterBrowserNoise) return event;

      if (isAbortError(hint.originalException)) return null;

      if (hasThirdPartyFrame(event)) {
        console.warn("Ignored third-party error:", event.message);

        return null;
      }

      return event;
    },
  };
};
